import { FC, useState, useEffect, useMemo, useContext } from 'react';
import {
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Box,
  TextField,
  FormControl,
  Fade,
  Grid,
  Divider,
  Typography
} from '@mui/material';
import { Subject, EmojiPeople, Comment, UploadFile } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ImageModal, Loader, ModalSaveSection, Modal, Card, Toast } from '../../../../components';
import { useQuery } from 'react-query';
import { DatePicker } from '@mui/x-date-pickers';
import {
  IPunchlistTodoItemDetail,
  IResponse,
  IVendor,
  IPunchlist,
  IPunchlistAssignToLookup,
  UserRoles,
  PunchlistGeneralAssignmentUserId
} from '../../../../models';
import { formatDate, formatDBFriendlyDateTime } from '../../../../helpers';
import {
  getPunchlistItem,
  postPunchlistItemComment,
  createPunchlistItemFiles,
  updatePunchlistTodo,
  getPunchlists,
  addPunchlistItem,
  createPunchlistImages,
  getPunchlistAssignToLookup
} from '../../../../fetch';
import { PunchlistCommentForm } from './PunchlistCommentForm';
import { PunchlistCommentList } from './PunchlistCommentList';
import { PunchlistCommentUpload } from './PunchlistCommentUpload';
import { PunchlistItemAssignedTo } from '../assign-to-dropdown';
import { TodoItemUploadsList } from './todo-item-uploads-list';
import { UserContext, VendorPageContext } from '../../../../context';
import { addDays } from 'date-fns';
import { useParams } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { getProductionVendors } from '../../../../fetch/lookups';

interface ITodoDetailModal {
  punchlistItemId?: string | number;
  handleClose: (shouldRefresh?: boolean, shouldClose?: boolean) => void;
  open: boolean;
  punchlistId?: number | string;
}

interface ISubscriberOption {
  id: string | number;
  accountName: string | null;
  displayName: string;
  personId: number | null;
  partnerId: number | null;
}

export const TodoDetailModal: FC<ITodoDetailModal> = ({
  punchlistItemId,
  open,
  handleClose,
  punchlistId
}) => {
  const classes = useStyles();
  const [isLoadingPunchlistItem, setIsLoadingPunchlistItem] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);
  const [statusUpdated, setStatusUpdated] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [todoItem, setTodoItem] = useState<IPunchlistTodoItemDetail>();
  const [punchlistComment, setPunchlistComment] = useState<string>('');
  const [selectedDueDate, setSelectedDueDate] = useState<Date | string>('');
  const [commentFiles, setCommentFiles] = useState<File[]>([]);
  const [todoFiles, setTodoFiles] = useState<File[]>([]);
  const [subscribeValues, setSubscribeValues] = useState<ISubscriberOption[]>([]);
  const [vendorValues, setVendorValues] = useState<IVendor[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [inputVendorValue, setInputVendorValue] = useState('');
  const [todoDescription, setTodoDescription] = useState('');
  const [itemIsAssociatedToFwtOrChecklist, setItemIsAssociatedToFwtOrChecklist] =
    useState<boolean>(false);
  const [selectedAssignee, setSelectedAssignee] = useState<IPunchlistAssignToLookup | null>(null);
  const [selectedPunchlist, setSelectedPunchlist] = useState<IPunchlist | null>(null);
  const [punchlistInput, setPunchlistInput] = useState('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [sendEmail, setSendEmail] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const { user, isAdmin, isFieldAdmin, isField } = useContext(UserContext);
  const { vendorSageId } = useContext(VendorPageContext);
  const { punchlistIdParam }: { punchlistIdParam: string } = useParams();
  const [generalAssignmentIsSelected, setGeneralAssignmentIsSelected] = useState(false);

  const { isLoading: isLoadingVendors, data: vendorsData } = useQuery<IResponse<IVendor[]>, Error>(
    ['additional-vendors', selectedPunchlist, todoItem],
    () =>
      getProductionVendors({
        perPage: '2147483647',
        sageJobId: todoItem?.sageJobId || selectedPunchlist?.sageJobId
      })
  );

  const territoryId = useMemo(
    () => todoItem?.territoryId ?? selectedPunchlist?.territoryId,
    [todoItem, selectedPunchlist]
  );
  const foundSageJobId = useMemo(
    () => todoItem?.sageJobId ?? selectedPunchlist?.sageJobId,
    [todoItem, selectedPunchlist]
  );

  const { isLoading: isLoadingAssignToItems, data: assignToData } = useQuery<
    IPunchlistAssignToLookup[],
    Error
  >(
    ['people-assignToItems', territoryId, foundSageJobId],
    async () =>
      getPunchlistAssignToLookup({
        territoryId,
        sageJobId: foundSageJobId
      }),
    {
      enabled: !!territoryId && !!foundSageJobId
    }
  );

  const assignToItems: IPunchlistAssignToLookup[] = useMemo(
    () =>
      assignToData?.map(item =>
        item?.isInternalUser ? { ...item, id: item?.accountName } : item
      ) ?? [],
    [assignToData]
  );

  const { isLoading: isLoadingPunchlists, data: punchlistsData } = useQuery<
    IResponse<IPunchlist[]>,
    Error
  >(['add-todo-punchlists'], () => getPunchlists({ perPage: '2147483647', sortBy: 'Name' }), {
    enabled: open
  });

  const punchlists: IPunchlist[] = useMemo(() => punchlistsData?.records ?? [], [punchlistsData]);
  const addtionalVendors: IVendor[] = useMemo(() => vendorsData?.records ?? [], [vendorsData]);

  useEffect(() => {
    if (selectedAssignee?.accountName === PunchlistGeneralAssignmentUserId) {
      setGeneralAssignmentIsSelected(true);
      setSendEmail(false);
    } else {
      setGeneralAssignmentIsSelected(false);
    }
  }, [selectedAssignee]);

  const fetchPunchlistItem = async (showLoader?: boolean, setTodo: boolean = true) => {
    if (punchlistItemId) {
      try {
        if (showLoader) {
          setIsLoadingPunchlistItem(true);
        }
        const res = await getPunchlistItem(punchlistItemId);
        if (setTodo) {
          setTodoItem(res);
        }
        setSelectedDueDate(res.dueDate ? new Date(res.dueDate) : undefined);
        setTodoDescription(res.description);
        setItemIsAssociatedToFwtOrChecklist(res.itemIsAssociatedToFwtOrChecklist ?? false);
        setIsCompleted(res.statusId === 2 ? true : false);
        setSelectedAssignee({
          accountName: res?.assignedTo ?? '',
          displayName: res?.assignedToDisplayName ?? '',
          id: null,
          isInternalUser: false,
          partnerId: null,
          personId: null
        });
      } catch (error) {
        enqueueSnackbar(error.response.data?.Detail, { variant: 'error' });
        handleClose();
      } finally {
        setIsLoadingPunchlistItem(false);
      }
    } else {
      setSelectedDueDate(addDays(new Date(), 7));
    }
  };
  useEffect(() => {
    if (todoItem && addtionalVendors) {
      const todoItemVendors = todoItem?.vendorSubscribers.map(v => v.vendorId);
      const selectedVendors = addtionalVendors?.filter(v => todoItemVendors.includes(v.vendorId));
      setVendorValues(selectedVendors ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addtionalVendors, todoItem]);

  useEffect(() => {
    if (open && punchlists) {
      setSelectedPunchlist(punchlists?.find(r => r.punchListId === punchlistId) ?? null);
    }
  }, [open, punchlists, setSelectedPunchlist, punchlistId]);

  useEffect(() => {
    if (open && punchlistItemId) {
      //If punchlistItemId is not a valid number, close the modal.
      const idNumber = Number(punchlistItemId);
      if (isNaN(idNumber)) {
        handleClose();
      }
      fetchPunchlistItem(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [punchlistItemId, open, selectedPunchlist]);

  useEffect(() => {
    if (todoItem && assignToItems) {
      const selected = assignToItems.filter(item =>
        todoItem?.subscribers?.includes(item.accountName)
      );
      setSubscribeValues(selected);
    }
  }, [assignToItems, todoItem]);

  const handleTodoFileUploads = async (): Promise<string[]> => {
    if (todoFiles?.length > 0) {
      try {
        const formData = new FormData();
        todoFiles.forEach(file => {
          formData.append('images', file);
        });
        return await createPunchlistImages(formData);
      } catch (error) {
        enqueueSnackbar(error.response.data?.Detail, { variant: 'error' });
      } finally {
        setTodoFiles([]);
        setIsDirty(false);
      }
    }
  };

  const handleCommentSave = async () => {
    if (punchlistComment || commentFiles?.length > 0) {
      try {
        const response = await postPunchlistItemComment(
          punchlistItemId,
          commentFiles?.length > 0 && !punchlistComment ? 'Files uploaded' : punchlistComment
        );
        if (commentFiles?.length > 0) {
          const formData = new FormData();
          commentFiles.forEach(file => {
            formData.append('files', file);
          });
          await createPunchlistItemFiles(response, formData);
        }
      } catch (error) {
        enqueueSnackbar(error.response.data?.Detail, { variant: 'error' });
      } finally {
        setPunchlistComment('');
        setCommentFiles([]);
        setIsDirty(false);
      }
    }
  };

  const clearState = async (saveAndContinue = false) => {
    setTodoItem(null);
    setIsDirty(false);
    setCommentFiles([]);
    setPunchlistComment('');
    setSubscribeValues([]);
    setVendorValues([]);
    setTodoDescription('');
    setSelectedDueDate(addDays(new Date(), 7));
    setInputVendorValue('');
    setSendEmail(true);
    if (saveAndContinue && punchlistIdParam !== 'dashboard') {
      setSelectedPunchlist(punchlists?.find(r => r.punchListId === punchlistId) ?? null);
    } else {
      setSelectedPunchlist(null);
    }
    if (!saveAndContinue) {
      setSelectedAssignee(null);
      await fetchPunchlistItem(true, false);
    } else {
      setSendEmail(!generalAssignmentIsSelected);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      const result = window.confirm('You have unsaved changes, are you sure you want to exit?');
      if (result) {
        handleClose();
      } else {
        return;
      }
    } else if (statusUpdated) {
      handleClose(true);
    } else {
      handleClose();
    }
    clearState();
  };
  const isFieldDisabled = todoItem?.createdBy?.includes('@')
    ? false
    : !!punchlistItemId && !isAdmin && !isFieldAdmin && !isField;

  const isEditingTodoItem = !!todoItem;
  const title = isLoadingPunchlistItem
    ? 'Loading...'
    : isEditingTodoItem
    ? 'Edit Todo'
    : 'Add Todo';
  const descriptionRows =
    todoDescription.length > 430
      ? 6
      : todoDescription.length > 325
      ? 5
      : todoDescription.length > 225
      ? 4
      : todoDescription.length > 125
      ? 3
      : 2;

  return (
    <Modal open={open} onClose={handleCancel} maxWidth="md" title={title}>
      <Fade in={open}>
        <div>
          <Card title="Details" icon={<Subject />}>
            <Box mt={isEditingTodoItem ? '-16px' : 1}>
              {(isLoadingPunchlistItem || isSaving) && (
                <Loader position="centered" type="overlay" />
              )}
              {isEditingTodoItem && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mt={0.5}
                  mb={0.5}
                >
                  <Typography color="primary">
                    Date Created:{' '}
                    <Typography color="secondary" component="span">
                      {formatDate(todoItem.createdOn)}
                    </Typography>
                  </Typography>
                  <FormControlLabel
                    color="primary"
                    control={
                      <Checkbox
                        checked={isCompleted}
                        id="todo-item-completed"
                        color="primary"
                        disabled={
                          isSaving ||
                          (user.userRoles.includes(UserRoles.BLVendor) &&
                            selectedAssignee?.accountName === PunchlistGeneralAssignmentUserId)
                        }
                        onClick={async e => {
                          const newValue = !isCompleted;
                          try {
                            setIsSaving(true);
                            setIsCompleted(newValue);
                            await updatePunchlistTodo(todoItem.punchlistItemId, {
                              ...todoItem,
                              isComplete: newValue,
                              subscribers: subscribeValues.map(val => val.accountName),
                              assignedTo: selectedAssignee?.accountName ?? '',
                              vendorSubscribers: vendorValues.map(v => v.vendorId),
                              photoGuids: []
                            });
                            enqueueSnackbar('Item updated!', {
                              variant: 'success'
                            });
                            setStatusUpdated(true);
                          } catch (error) {
                            enqueueSnackbar(
                              error?.Detail ?? 'Error saving item, please try again.',
                              {
                                variant: 'error'
                              }
                            );
                            setIsCompleted(!newValue);
                          } finally {
                            setIsSaving(false);
                          }
                        }}
                      />
                    }
                    label="Completed"
                  />
                </Box>
              )}
              <Grid container spacing={1}>
                {!isEditingTodoItem && !isLoadingPunchlistItem && (
                  <>
                    <Grid
                      item
                      xs={12}
                      justifyContent="space-between"
                      alignItems="center"
                      display="flex"
                      flexDirection="row"
                    >
                      <Typography>
                        Select a punchlist first before filling out the rest of the fields.
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={sendEmail}
                            id="send-email-checkbox"
                            onChange={() => {
                              setSendEmail(!sendEmail);
                            }}
                            disabled={generalAssignmentIsSelected}
                          />
                        }
                        label="Send Email"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small" required>
                        <Autocomplete
                          id="punchlist"
                          value={selectedPunchlist}
                          onChange={(event, newValue: IPunchlist) => {
                            setIsDirty(true);
                            setSelectedPunchlist(newValue);
                          }}
                          inputValue={punchlistInput}
                          onInputChange={(event, newInputValue) => {
                            setPunchlistInput(newInputValue);
                          }}
                          options={punchlists}
                          getOptionLabel={(option: any) => option?.punchListName ?? option}
                          filterSelectedOptions
                          renderOption={(props, option: IPunchlist) => {
                            return (
                              <li {...props} key={`${option.punchListId}`}>
                                {option.punchListName}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              disabled={isLoadingPunchlists}
                              label={isLoadingPunchlists ? 'Loading...' : 'Punchlist'}
                              size="small"
                              required
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={isEditingTodoItem && !isLoadingPunchlistItem ? 12 : 6}>
                  <FormControl fullWidth variant="outlined" size="small" required>
                    <PunchlistItemAssignedTo
                      territoryId={todoItem?.territoryId ?? selectedPunchlist?.territoryId}
                      handleAssignToChange={() => setIsDirty(true)}
                      isEnabled={open && (!!selectedPunchlist || !!todoItem)}
                      selectedAssignee={selectedAssignee}
                      setSelectedAssignee={setSelectedAssignee}
                      sageJobId={selectedPunchlist?.sageJobId || todoItem?.sageJobId}
                      includeGeneralAssignment={!user.userRoles.includes(UserRoles.BLVendor)}
                      overrideDisabled={
                        user.userRoles.includes(UserRoles.BLVendor) &&
                        selectedAssignee?.accountName === PunchlistGeneralAssignmentUserId
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    autoComplete="nope"
                    label="Description"
                    name="description"
                    multiline
                    disabled={isFieldDisabled}
                    rows={descriptionRows}
                    value={todoDescription}
                    size="small"
                    className={
                      itemIsAssociatedToFwtOrChecklist
                        ? classes.redResizableTextarea
                        : classes.resizableTextarea
                    }
                    onChange={e => {
                      if (e.target.value.length > 500) {
                        setError(true);
                      } else {
                        setIsDirty(true);
                        setTodoDescription(e.target.value);
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl required fullWidth variant="outlined">
                    <DatePicker
                      label="Due Date"
                      value={selectedDueDate}
                      disabled={isFieldDisabled}
                      onChange={date => {
                        if (date === null) {
                          setSelectedDueDate(null);
                          return;
                        }
                        setSelectedDueDate(date);
                        setIsDirty(true);
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: false
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Card>
          <Card title="Additional Vendors" icon={<EmojiPeople />}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="vendor-subscribers"
                  value={vendorValues}
                  onChange={(event, newValue: any[]) => {
                    setIsDirty(true);
                    setVendorValues(newValue);
                  }}
                  inputValue={inputVendorValue}
                  onInputChange={(event, newInputValue) => {
                    setInputVendorValue(newInputValue);
                  }}
                  disabled={isFieldDisabled}
                  options={addtionalVendors}
                  getOptionLabel={option => option.name}
                  filterSelectedOptions
                  renderOption={(props, option: IVendor) => {
                    return (
                      <li {...props} key={`${option.vendorId}`}>
                        {option.name}
                      </li>
                    );
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      disabled={isLoadingVendors}
                      label={isLoadingVendors ? 'Loading...' : 'Companies'}
                      placeholder="Add vendors to this To-do item"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="item-subscribers"
                  value={subscribeValues}
                  onChange={(event, newValue: any[]) => {
                    setIsDirty(true);
                    setSubscribeValues(newValue);
                  }}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  disabled={isFieldDisabled}
                  options={assignToItems}
                  getOptionLabel={option => option.displayName}
                  filterSelectedOptions
                  renderOption={(props, option: IPunchlistAssignToLookup) => {
                    return (
                      <li {...props} key={`${option.id}`}>
                        {option.displayName}
                      </li>
                    );
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      disabled={isLoadingAssignToItems}
                      label={isLoadingAssignToItems ? 'Loading...' : 'People'}
                      placeholder="Add people to this To-do item"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Card>
          {isEditingTodoItem && (
            <Card title="Comments" icon={<Comment />}>
              {todoItem?.comments?.length > 0 && (
                <PunchlistCommentList punchlistComments={todoItem.comments} />
              )}
              <PunchlistCommentForm
                punchlistComment={punchlistComment}
                setPunchlistComment={setPunchlistComment}
                commentFiles={commentFiles}
                setCommentFiles={setCommentFiles}
                setIsDirty={setIsDirty}
                uploadText="Attach file(s) to a comment"
              />
            </Card>
          )}
          <Card title="Uploads" icon={<UploadFile />}>
            {todoItem?.punchlistItemFiles?.length > 0 && (
              <>
                <TodoItemUploadsList
                  files={todoItem.punchlistItemFiles}
                  setSelectedImage={(val: string) => setSelectedImage(val)}
                  setSelectedPdf={async (url: string) => {
                    fetch(url)
                      .then(response => response.blob())
                      .then(blob => {
                        let newBlob = new Blob([blob], { type: 'application/pdf' });
                        setPdfBlobUrl(URL.createObjectURL(newBlob));
                      });
                  }}
                />
                <Divider />
              </>
            )}
            <PunchlistCommentUpload
              files={todoFiles}
              uploadText="Attach file(s) to this item"
              uploadAccept="image/*"
              setFiles={files => {
                setTodoFiles(files);
                if (files?.length > 0) {
                  setIsDirty(true);
                } else {
                  setIsDirty(false);
                }
              }}
            />
          </Card>
          <ModalSaveSection
            enableSaveAndContinue={!isEditingTodoItem}
            handleCancel={handleCancel}
            isSaveDisabled={
              !isEditingTodoItem
                ? !todoDescription || !selectedAssignee || !selectedPunchlist
                : !isDirty
            }
            handleSave={async (saveAndContinue = false) => {
              try {
                setIsSaving(true);
                // creating a new todo item
                const guids = await handleTodoFileUploads();
                if (!isEditingTodoItem) {
                  await addPunchlistItem(selectedPunchlist.punchListId ?? punchlistId, {
                    assignedTo: selectedAssignee.accountName,
                    description: todoDescription,
                    dueDate: formatDBFriendlyDateTime(selectedDueDate) ?? null,
                    subscribers: subscribeValues.map(val => val.accountName),
                    vendorSubscribers: vendorValues.map(v => v.vendorId),
                    partnerId: selectedAssignee?.partnerId,
                    sortOrder: null,
                    isComplete: false,
                    photoGuids: guids ?? [],
                    shouldSendCreationEmail: sendEmail
                  });
                  enqueueSnackbar(`To-do added!`, {
                    variant: 'success'
                  });
                  if (!saveAndContinue) {
                    handleClose(true);
                  } else {
                    handleClose(true, false);
                  }
                  await clearState(saveAndContinue);
                  // editing an existing todo item
                } else {
                  handleCommentSave();
                  let subscribers = subscribeValues.map(val => val.accountName);
                  if (punchlistComment || commentFiles?.length > 0) {
                    subscribers = Array.from(new Set([...subscribers, user.userName]));
                  }
                  await updatePunchlistTodo(
                    todoItem.punchlistItemId,
                    {
                      ...todoItem,
                      dueDate: formatDBFriendlyDateTime(selectedDueDate),
                      isComplete: isCompleted,
                      subscribers,
                      vendorSubscribers: vendorValues.map(v => v.vendorId),
                      assignedTo: selectedAssignee?.accountName,
                      // This is to ensure partnerId remains the same (unless the assignee is changed) and not cleared on edit
                      partnerId:
                        todoItem?.assignedTo !== selectedAssignee?.accountName
                          ? selectedAssignee?.partnerId
                          : todoItem?.partnerId,
                      description: todoDescription,
                      photoGuids: guids ?? []
                    },
                    vendorSageId
                  );
                  enqueueSnackbar('Item updated!', {
                    variant: 'success'
                  });
                  fetchPunchlistItem();
                  setIsDirty(false);
                  setSubscribeValues([]);
                  setSelectedDueDate('');
                  setSelectedAssignee(null);
                  setSelectedPunchlist(null);
                  setCommentFiles([]);
                  setVendorValues([]);
                  setInputVendorValue('');
                  handleClose(true);
                }
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? 'Error saving item, please try again.', {
                  variant: 'error'
                });
              } finally {
                setIsSaving(false);
              }
            }}
          />
        </div>
      </Fade>
      <Toast
        id="attorney-error"
        autoHideDuration={5000}
        message={'Description cannot exceed 500 characters.'}
        open={error}
        onClose={() => setError(false)}
        variant="error"
      />
      <ImageModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage('')}
        source={selectedImage}
      />
      <Modal
        open={!!pdfBlobUrl}
        onClose={() => {
          setPdfBlobUrl('');
        }}
        maxWidth="md"
      >
        <Box width="100%" mt={1}>
          <iframe
            width="100%"
            height="600"
            src={pdfBlobUrl ? `${pdfBlobUrl}#toolbar=0` : ''}
            title="Punchlist PDF"
          ></iframe>
        </Box>
      </Modal>
    </Modal>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  resizableTextarea: {
    '& textarea': {
      resize: 'vertical',
      overflow: 'auto'
    }
  },
  redResizableTextarea: {
    '& textarea': {
      resize: 'vertical',
      overflow: 'auto',
      color: theme.palette.error.main
    }
  }
}));
